import type { Id } from "@repo/convex/dataModel";
import { CheckCircle2, ChevronRight, CircleCheck, MessageSquarePlus } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { MessageMarkdown } from "@/components/ui/message-markdown";
import { ScrollShadow } from "@/components/ui/scroll-shadow";
import { StatusPanel } from "@/components/ui/status-panel";
import { useClarificationRequestMessages } from "@/features/clarification/hooks";
import { formatFieldName } from "@/features/clarification/utils/field-utils";
import type { ClarificationRequestFilter } from "@/features/project/types";
import { formatSmartDate } from "@/lib/formatters";
import { cn } from "@/lib/utils";

type ClarificationStatus = "open" | "closed";

interface ClarificationWithDates {
  id: string;
  title: string;
  status: ClarificationStatus;
  author: string;
  authorInitials: string;
  createdAt: Date;
  lastActivity: Date;
  fieldId?: string;
  highlightedText?: string;
}

interface ClarificationListViewProps {
  clarifications: ClarificationWithDates[];
  filter: ClarificationRequestFilter;
  onFilterChange: (filter: ClarificationRequestFilter) => void;
  onClarificationOpen: (id: string) => void;
  linkedClarificationIds?: string[];
  linkedPrimaryClarificationId?: string | null;
  linkedActivationKey?: number;
  /** When true, group clarifications by field ID with section headers */
  groupByField?: boolean;
  /** Entity name shown in the header when entity-scoped */
  entityName?: string | null;
}

const filterDisplayLabel: Record<ClarificationRequestFilter, string> = {
  open: "Open",
  all: "All",
  closed: "Resolved",
};

export function ClarificationListView({
  clarifications,
  filter,
  onFilterChange,
  onClarificationOpen,
  linkedClarificationIds = [],
  linkedPrimaryClarificationId = null,
  linkedActivationKey = 0,
  groupByField = false,
}: ClarificationListViewProps) {
  const linkedClarificationIdSet = useMemo(
    () => new Set(linkedClarificationIds),
    [linkedClarificationIds]
  );
  const filteredClarifications = clarifications.filter((clarification) =>
    filter === "all" ? true : clarification.status === filter
  );

  // Group clarifications by fieldId when in entity-scoped mode
  const groupedClarifications = useMemo(() => {
    if (!groupByField) return null;

    const groups = new Map<string, ClarificationWithDates[]>();
    for (const q of filteredClarifications) {
      const key = q.fieldId ?? "_unscoped";
      const existing = groups.get(key);
      if (existing) {
        existing.push(q);
      } else {
        groups.set(key, [q]);
      }
    }
    return groups;
  }, [groupByField, filteredClarifications]);

  // Empty State A — no CRs at all for this entity
  if (clarifications.length === 0) {
    return <EmptyStateNoClarifications />;
  }

  // Determine if "all caught up" empty state should show
  const showAllCaughtUp =
    filteredClarifications.length === 0 &&
    filter === "open" &&
    clarifications.some((clarification) => clarification.status === "closed");

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <ScrollShadow
        className="min-h-0 flex-1"
        colorScheme="raised"
        contentClassName="min-h-full flex flex-col"
      >
        {/* Filter pills */}
        <div className="flex gap-1.5 px-3 py-2 shrink-0">
          {(["open", "all", "closed"] as const).map((filterOption) => {
            const count =
              filterOption === "all"
                ? clarifications.length
                : clarifications.filter((clarification) => clarification.status === filterOption)
                    .length;
            const isActive = filter === filterOption;
            return (
              <button
                key={filterOption}
                type="button"
                aria-pressed={isActive}
                aria-label={`${filterDisplayLabel[filterOption]} requests (${count})`}
                onClick={() => onFilterChange(filterOption)}
                className={cn(
                  "h-[26px] px-2.5 text-[11px] font-medium inline-flex items-center justify-center border",
                  isActive
                    ? "bg-blue-600 text-white border-blue-600"
                    : "border-border bg-background text-muted-foreground hover:bg-accent"
                )}
              >
                {filterDisplayLabel[filterOption]} {count}
              </button>
            );
          })}
        </div>

        {showAllCaughtUp ? (
          <EmptyStateAllResolved />
        ) : filteredClarifications.length === 0 ? (
          <div className="flex-1 text-center py-8 px-6">
            <p className="text-[11px] text-muted-foreground">
              No {filterDisplayLabel[filter].toLowerCase()} clarification requests
            </p>
          </div>
        ) : groupedClarifications ? (
          <GroupedClarificationList
            groups={groupedClarifications}
            linkedActivationKey={linkedActivationKey}
            linkedPrimaryClarificationId={linkedPrimaryClarificationId}
            linkedClarificationIdSet={linkedClarificationIdSet}
            onClarificationOpen={onClarificationOpen}
          />
        ) : (
          <ul aria-label={`${filter} clarification requests`} className="m-0 list-none p-0">
            {filteredClarifications.map((clarification) => (
              <ClarificationItem
                key={clarification.id}
                isLinked={linkedClarificationIdSet.has(clarification.id)}
                isPrimaryLinked={linkedPrimaryClarificationId === clarification.id}
                linkedActivationKey={linkedActivationKey}
                onOpen={onClarificationOpen}
                clarification={clarification}
              />
            ))}
          </ul>
        )}
      </ScrollShadow>
    </div>
  );
}

// ============================================================================
// Empty state: no clarification requests at all
// ============================================================================

function EmptyStateNoClarifications() {
  return (
    <StatusPanel
      className="h-full px-6 py-8"
      size="compact"
      icon={MessageSquarePlus}
      title="No clarification requests yet"
    >
      <div className="flex max-w-[220px] flex-col items-center gap-1.5">
        <p className="inline-flex flex-wrap items-center justify-center gap-1 text-center text-muted-foreground text-xs">
          Select content and click{" "}
          <MessageSquarePlus className="inline size-3.5 text-muted-foreground" />
        </p>
        <p className="text-center text-muted-foreground text-xs">to request clarification.</p>
      </div>
    </StatusPanel>
  );
}

// ============================================================================
// Empty state: open filter active, all resolved
// ============================================================================

function EmptyStateAllResolved() {
  return (
    <StatusPanel
      className="h-full px-6 py-8"
      size="compact"
      tone="success"
      icon={CircleCheck}
      title="All caught up!"
      description="All clarification requests have been resolved. Switch to All to review past threads."
    >
      <div className="flex max-w-[220px] flex-col items-center gap-1.5 pt-4">
        <div className="bg-border h-px w-[120px]" />
        <div className="flex flex-col items-center gap-1.5 pt-1">
          <p className="text-muted-foreground inline-flex flex-wrap items-center justify-center gap-1 text-center text-[11px]">
            Select content and click{" "}
            <MessageSquarePlus className="text-muted-foreground inline size-3" />
          </p>
          <p className="text-center text-muted-foreground text-[11px]">
            to start a new clarification request.
          </p>
        </div>
      </div>
    </StatusPanel>
  );
}

// ============================================================================
// Grouped clarification list (entity-scoped view)
// ============================================================================

function GroupedClarificationList({
  groups,
  linkedClarificationIdSet,
  linkedPrimaryClarificationId,
  linkedActivationKey,
  onClarificationOpen,
}: {
  groups: Map<string, ClarificationWithDates[]>;
  linkedClarificationIdSet: Set<string>;
  linkedPrimaryClarificationId: string | null;
  linkedActivationKey: number;
  onClarificationOpen: (id: string) => void;
}) {
  const entries = Array.from(groups.entries());

  return (
    <div>
      {entries.map(([fieldKey, clarifications], index) => {
        const label = fieldKey === "_unscoped" ? "General" : formatFieldName(fieldKey);

        return (
          <div key={fieldKey}>
            {index > 0 && <div className="bg-border h-px" />}
            <div className="flex items-center h-8 px-4">
              <span className="text-muted-foreground text-[11px] font-semibold">{label}</span>
            </div>
            <ul aria-label={`${label} clarifications`} className="m-0 list-none p-0">
              {clarifications.map((clarification) => (
                <ClarificationItem
                  key={clarification.id}
                  isLinked={linkedClarificationIdSet.has(clarification.id)}
                  isPrimaryLinked={linkedPrimaryClarificationId === clarification.id}
                  linkedActivationKey={linkedActivationKey}
                  onOpen={onClarificationOpen}
                  clarification={clarification}
                />
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Individual clarification item (shared between flat and grouped views)
// ============================================================================

function ClarificationItem({
  isLinked,
  isPrimaryLinked,
  linkedActivationKey,
  clarification,
  onOpen,
}: {
  isLinked: boolean;
  isPrimaryLinked: boolean;
  linkedActivationKey: number;
  clarification: ClarificationWithDates;
  onOpen: (id: string) => void;
}) {
  const isResolved = clarification.status === "closed";
  const messages = useClarificationRequestMessages(clarification.id as Id<"clarificationRequests">);
  const replyCount = Math.max(0, messages?.length ?? 0);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const pulseTimeoutRef = useRef<number | null>(null);
  const [isPulseActive, setIsPulseActive] = useState(false);

  useEffect(() => {
    if (!isLinked || linkedActivationKey === 0) {
      setIsPulseActive(false);
      return;
    }

    setIsPulseActive(true);
    if (pulseTimeoutRef.current) {
      window.clearTimeout(pulseTimeoutRef.current);
    }
    pulseTimeoutRef.current = window.setTimeout(() => {
      setIsPulseActive(false);
      pulseTimeoutRef.current = null;
    }, 1400);

    return () => {
      if (pulseTimeoutRef.current) {
        window.clearTimeout(pulseTimeoutRef.current);
        pulseTimeoutRef.current = null;
      }
    };
  }, [isLinked, linkedActivationKey]);

  useEffect(() => {
    if (!isPrimaryLinked || linkedActivationKey === 0) {
      return;
    }
    buttonRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
  }, [isPrimaryLinked, linkedActivationKey]);

  return (
    <li>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => onOpen(clarification.id)}
        aria-label={`${clarification.title} by ${clarification.author}, ${clarification.status}`}
        data-clarification-linked={isLinked ? "true" : undefined}
        data-clarification-primary-linked={isPrimaryLinked ? "true" : undefined}
        className={cn(
          "w-full px-4 py-2.5 text-left transition-colors focus-visible:outline-none",
          isLinked && "linked-clarification-row",
          isPulseActive && "linked-clarification-row-pulse",
          isResolved
            ? "opacity-55 hover:bg-accent/40 focus-visible:bg-accent/40"
            : "hover:bg-accent/60 focus-visible:bg-accent/60"
        )}
      >
        {/* Top row: author + timestamp */}
        <div className="flex items-center gap-2.5 mb-1.5">
          {isResolved && (
            <CheckCircle2
              className="text-muted-foreground size-3.5 shrink-0"
              aria-label="Resolved"
            />
          )}
          <span className="text-foreground/80 truncate text-[11px] font-medium">
            {clarification.author}
          </span>
          <span className="flex-1" />
          <time
            dateTime={clarification.lastActivity.toISOString()}
            className="text-muted-foreground shrink-0 text-xs"
          >
            {formatSmartDate(clarification.lastActivity.getTime())}
          </time>
        </div>

        {/* Indented content below author row */}
        <div className="pl-3">
          {/* Quote block (highlighted text) */}
          {clarification.highlightedText && (
            <p className="text-muted-foreground mb-1.5 line-clamp-3 border-l-2 border-border pl-2 text-xs italic">
              {clarification.highlightedText}
            </p>
          )}

          {/* Clarification text */}
          <MessageMarkdown
            content={clarification.title}
            className="text-foreground/80 text-[13px] leading-[1.4] [&_ol]:my-0 [&_p]:mb-0 [&_p]:line-clamp-2 [&_ul]:my-0"
          />

          {/* Reply link */}
          {replyCount > 0 && (
            <span className="text-primary mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium">
              Show {replyCount} {replyCount === 1 ? "reply" : "replies"}
              <ChevronRight className="size-3" />
            </span>
          )}
        </div>
      </button>
    </li>
  );
}
